/**
 * 스케줄 공지(SCHEDULE_NOTICE) 제약사·마감일 유틸.
 * 레거시 `announcementSchedulePharma.js` 포팅.
 */
import type { PharmaCompanyOption } from '@/pages/board/PharmaCompanySelectModal';
import {
  composeDateTimeLocalValue as composeDatetimeLocal,
  splitDateTimeLocalValue as splitDatetimeLocal,
} from '@/shared/lib/datePickerUtils';

export { composeDatetimeLocal, splitDatetimeLocal };

export const PHARMA_SELECTION_TYPE = {
  SELECTED: 'SELECTED',
  ALL: 'ALL',
  OTHERS: 'OTHERS',
} as const;

export type PharmaSelectionType =
  (typeof PHARMA_SELECTION_TYPE)[keyof typeof PHARMA_SELECTION_TYPE];

export const PHARMA_SELECTION_TYPE_LABEL: Record<PharmaSelectionType, string> = {
  [PHARMA_SELECTION_TYPE.ALL]: '전체 제약사',
  [PHARMA_SELECTION_TYPE.OTHERS]: '그 외 전체 제약사',
  [PHARMA_SELECTION_TYPE.SELECTED]: '',
};

export const PHARMA_SELECTION_SPECIAL_OPTIONS = [
  {
    pharmaSelectionType: PHARMA_SELECTION_TYPE.ALL,
    label: PHARMA_SELECTION_TYPE_LABEL[PHARMA_SELECTION_TYPE.ALL],
  },
  {
    pharmaSelectionType: PHARMA_SELECTION_TYPE.OTHERS,
    label: PHARMA_SELECTION_TYPE_LABEL[PHARMA_SELECTION_TYPE.OTHERS],
  },
] as const;

export interface ScheduleFormItem {
  deadlineDate: string;
  pharmaSelectionType: PharmaSelectionType;
  pharmaCompanies: PharmaCompanyOption[];
}

export interface ScheduleDeadlinePayload {
  deadlineDate: string;
  pharmaSelectionType: PharmaSelectionType;
  pharmaCompanyIds: number[];
}

export interface AnnouncementSchedulePayload {
  yearMonth: string;
  topContent: string | null;
  bottomContent: string | null;
  deadlines: ScheduleDeadlinePayload[];
}

export const isSamePharmaCompanyId = (left: unknown, right: unknown) =>
  String(left) === String(right);

export const normalizeSchedulePharmaCompany = (
  company: Partial<PharmaCompanyOption> & { id?: number; name?: string },
): PharmaCompanyOption => {
  const pharmaCompanyId = company.pharmaCompanyId ?? company.id ?? 0;
  return {
    pharmaCompanyId,
    pharmaCompanyName:
      company.pharmaCompanyName ??
      company.name ??
      `제약사 #${pharmaCompanyId}`,
  };
};

export const mapPharmaCompaniesToIds = (companies: PharmaCompanyOption[] = []) =>
  companies.map((company) => company.pharmaCompanyId);

const pad2 = (n: number) => String(n).padStart(2, '0');

export const formatYearMonth = (value: string | Date | null | undefined): string | null => {
  if (!value) return null;
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}$/.test(value)) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
  }
  return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}`;
};

export const formatScheduleDeadlineDate = (
  value: string | Date | null | undefined,
): string | null => {
  if (!value) return null;
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }
  return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
};

export const getDefaultScheduleYearMonth = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1, 1);
  return formatYearMonth(d) ?? '';
};

export const getDefaultScheduleDeadlineDate = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1, 10);
  d.setHours(0, 0, 0, 0);
  return formatScheduleDeadlineDate(d) ?? '';
};

export const createEmptyScheduleItem = (): ScheduleFormItem => ({
  deadlineDate: getDefaultScheduleDeadlineDate(),
  pharmaSelectionType: PHARMA_SELECTION_TYPE.SELECTED,
  pharmaCompanies: [],
});

export const mapScheduleDeadlineToFormItem = (
  deadline: {
    deadlineDate?: string;
    pharmaSelectionType?: PharmaSelectionType;
    pharmaCompanies?: PharmaCompanyOption[];
    pharmaCompanyIds?: number[];
  } = {},
): ScheduleFormItem => {
  const pharmaSelectionType =
    deadline.pharmaSelectionType ?? PHARMA_SELECTION_TYPE.SELECTED;

  let pharmaCompanies: PharmaCompanyOption[] = [];
  if (pharmaSelectionType === PHARMA_SELECTION_TYPE.SELECTED) {
    if (deadline.pharmaCompanies?.length) {
      pharmaCompanies = deadline.pharmaCompanies.map(normalizeSchedulePharmaCompany);
    } else if (deadline.pharmaCompanyIds?.length) {
      pharmaCompanies = deadline.pharmaCompanyIds.map((id) => ({
        pharmaCompanyId: id,
        pharmaCompanyName: `제약사 #${id}`,
      }));
    }
  }

  return {
    deadlineDate: formatScheduleDeadlineDate(deadline.deadlineDate) ?? '',
    pharmaSelectionType,
    pharmaCompanies,
  };
};

export const mapScheduleItemsFromAnnouncement = (announcement: {
  schedule?: {
    deadlines?: Array<{
      deadlineDate?: string;
      pharmaSelectionType?: PharmaSelectionType;
      pharmaCompanies?: PharmaCompanyOption[];
      pharmaCompanyIds?: number[];
    }>;
    yearMonth?: string;
  };
  registrationScheduleItems?: Array<{
    deadlineDate?: string;
    pharmaSelectionType?: PharmaSelectionType;
    pharmaCompanies?: PharmaCompanyOption[];
  }>;
  registrationDeadlines?: string[];
}): ScheduleFormItem[] => {
  if (announcement.schedule?.deadlines?.length) {
    return announcement.schedule.deadlines.map(mapScheduleDeadlineToFormItem);
  }
  if (announcement.registrationScheduleItems?.length) {
    return announcement.registrationScheduleItems.map(mapScheduleDeadlineToFormItem);
  }
  if (announcement.registrationDeadlines?.length) {
    return announcement.registrationDeadlines.map((deadline) => ({
      deadlineDate: formatScheduleDeadlineDate(deadline) ?? '',
      pharmaSelectionType: PHARMA_SELECTION_TYPE.SELECTED,
      pharmaCompanies: [],
    }));
  }
  return [createEmptyScheduleItem()];
};

export const buildAnnouncementSchedulePayload = (input: {
  registrationYearMonth: string;
  scheduleTopContent?: string;
  scheduleBottomContent?: string;
  registrationScheduleItems: ScheduleFormItem[];
}): AnnouncementSchedulePayload => {
  const topContent = input.scheduleTopContent?.trim();
  const bottomContent = input.scheduleBottomContent?.trim();

  return {
    yearMonth: formatYearMonth(input.registrationYearMonth) ?? '',
    topContent: topContent || null,
    bottomContent: bottomContent || null,
    deadlines: (input.registrationScheduleItems || [])
      .filter((item) => item?.deadlineDate)
      .map((item) => {
        const pharmaSelectionType =
          item.pharmaSelectionType || PHARMA_SELECTION_TYPE.SELECTED;
        return {
          deadlineDate: formatScheduleDeadlineDate(item.deadlineDate) ?? '',
          pharmaSelectionType,
          pharmaCompanyIds:
            pharmaSelectionType === PHARMA_SELECTION_TYPE.SELECTED
              ? mapPharmaCompaniesToIds(item.pharmaCompanies || [])
              : [],
        };
      }),
  };
};

export const getSchedulePharmaSelectionConstraints = (
  scheduleItems: ScheduleFormItem[],
  activeIndex: number,
) => {
  let hasAllPharmaElsewhere = false;
  let hasOtherPharmaElsewhere = false;
  let hasIndividualPharmaElsewhere = false;
  const selectedIndividualPharmaIdsElsewhere = new Set<string>();

  scheduleItems.forEach((item, index) => {
    if (index === activeIndex) return;

    const pharmaSelectionType =
      item?.pharmaSelectionType || PHARMA_SELECTION_TYPE.SELECTED;

    if (pharmaSelectionType === PHARMA_SELECTION_TYPE.ALL) {
      hasAllPharmaElsewhere = true;
    }
    if (pharmaSelectionType === PHARMA_SELECTION_TYPE.OTHERS) {
      hasOtherPharmaElsewhere = true;
    }
    if (pharmaSelectionType === PHARMA_SELECTION_TYPE.SELECTED) {
      (item?.pharmaCompanies || []).forEach((company) => {
        hasIndividualPharmaElsewhere = true;
        selectedIndividualPharmaIdsElsewhere.add(String(company.pharmaCompanyId));
      });
    }
  });

  return {
    hasAllPharmaElsewhere,
    hasOtherPharmaElsewhere,
    hasIndividualPharmaElsewhere,
    selectedIndividualPharmaIdsElsewhere: [
      ...selectedIndividualPharmaIdsElsewhere,
    ],
  };
};

export const validateScheduleRegistrationItems = (
  scheduleItems: ScheduleFormItem[],
): { valid: boolean; message?: string } => {
  const items = (scheduleItems || []).filter((item) => item?.deadlineDate);

  if (items.length === 0) {
    return {
      valid: false,
      message: '등록 마감일(deadlines)은 1건 이상 필요합니다.',
    };
  }

  const dates = items
    .map((item) => formatScheduleDeadlineDate(item.deadlineDate))
    .filter(Boolean) as string[];

  if (dates.length !== new Set(dates).size) {
    return { valid: false, message: '등록 마감일은 중복될 수 없습니다.' };
  }

  let allPharmaDeadlineIndex = -1;
  let otherPharmaDeadlineIndex = -1;
  const individualIdToCompany = new Map<string, PharmaCompanyOption>();

  for (let index = 0; index < items.length; index += 1) {
    const pharmaSelectionType =
      items[index]?.pharmaSelectionType || PHARMA_SELECTION_TYPE.SELECTED;

    if (pharmaSelectionType === PHARMA_SELECTION_TYPE.ALL) {
      if (allPharmaDeadlineIndex !== -1) {
        return {
          valid: false,
          message: '전체 제약사는 하나의 마감일에만 선택할 수 있습니다.',
        };
      }
      allPharmaDeadlineIndex = index;
      continue;
    }

    if (pharmaSelectionType === PHARMA_SELECTION_TYPE.OTHERS) {
      if (otherPharmaDeadlineIndex !== -1) {
        return {
          valid: false,
          message: '그 외 전체 제약사는 하나의 마감일에만 선택할 수 있습니다.',
        };
      }
      otherPharmaDeadlineIndex = index;
      continue;
    }

    for (const company of items[index]?.pharmaCompanies || []) {
      const companyId = String(company.pharmaCompanyId);
      if (individualIdToCompany.has(companyId)) {
        return {
          valid: false,
          message: `${company.pharmaCompanyName || '제약사'}는 다른 마감일에 이미 선택되어 있습니다.`,
        };
      }
      individualIdToCompany.set(companyId, company);
    }
  }

  if (allPharmaDeadlineIndex !== -1) {
    for (let index = 0; index < items.length; index += 1) {
      if (index === allPharmaDeadlineIndex) continue;

      const pharmaSelectionType =
        items[index]?.pharmaSelectionType || PHARMA_SELECTION_TYPE.SELECTED;

      if (
        pharmaSelectionType === PHARMA_SELECTION_TYPE.ALL ||
        pharmaSelectionType === PHARMA_SELECTION_TYPE.OTHERS ||
        (items[index]?.pharmaCompanies || []).length > 0
      ) {
        return {
          valid: false,
          message:
            '전체 제약사가 선택된 경우 다른 마감일에는 제약사를 선택할 수 없습니다.',
        };
      }
    }
  }

  return { valid: true };
};

export interface ScheduleDisplayTag {
  key: string;
  label: string;
  pharmaSelectionType?: PharmaSelectionType;
  pharmaCompanyId?: number;
  removable: boolean;
}

export const getScheduleDeadlineDisplayTags = (
  item: Partial<ScheduleFormItem> = {},
): ScheduleDisplayTag[] => {
  const pharmaSelectionType =
    item.pharmaSelectionType || PHARMA_SELECTION_TYPE.SELECTED;

  if (pharmaSelectionType === PHARMA_SELECTION_TYPE.ALL) {
    return [
      {
        key: PHARMA_SELECTION_TYPE.ALL,
        label: PHARMA_SELECTION_TYPE_LABEL[PHARMA_SELECTION_TYPE.ALL],
        pharmaSelectionType: PHARMA_SELECTION_TYPE.ALL,
        removable: true,
      },
    ];
  }

  if (pharmaSelectionType === PHARMA_SELECTION_TYPE.OTHERS) {
    return [
      {
        key: PHARMA_SELECTION_TYPE.OTHERS,
        label: PHARMA_SELECTION_TYPE_LABEL[PHARMA_SELECTION_TYPE.OTHERS],
        pharmaSelectionType: PHARMA_SELECTION_TYPE.OTHERS,
        removable: true,
      },
    ];
  }

  return (item.pharmaCompanies || []).map((company) => ({
    key: String(company.pharmaCompanyId),
    label: company.pharmaCompanyName,
    removable: true,
    pharmaCompanyId: company.pharmaCompanyId,
  }));
};

export const isAnnouncementContentEmpty = (content: string | null | undefined) => {
  if (content == null) return true;
  const trimmed = String(content).trim();
  if (!trimmed) return true;
  const textOnly = trimmed
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return !textOnly;
};

/**
 * 레거시 removeTableStyles — table 및 하위 style의 height만 제거.
 * TinyMCE 표 높이 고정이 모바일/웹뷰에서 잘리는 문제를 막는다.
 */
export const removeAnnouncementTableHeightStyles = (html: string): string => {
  if (!html || typeof document === 'undefined') return html;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const stripHeight = (style: string) =>
      style
        .split(';')
        .map((prop) => prop.trim())
        .filter((prop) => prop && !/^height\s*:/i.test(prop))
        .join('; ')
        .replace(/;\s*;/g, ';')
        .replace(/^;\s*|\s*;$/g, '');

    doc.querySelectorAll('table').forEach((table) => {
      const currentStyle = table.getAttribute('style');
      if (currentStyle) {
        const cleaned = stripHeight(currentStyle);
        if (cleaned) table.setAttribute('style', cleaned);
        else table.removeAttribute('style');
      }

      table.querySelectorAll('*').forEach((element) => {
        const elementStyle = element.getAttribute('style');
        if (!elementStyle) return;
        const cleaned = stripHeight(elementStyle);
        if (cleaned) element.setAttribute('style', cleaned);
        else element.removeAttribute('style');
      });
    });

    return doc.body.innerHTML;
  } catch {
    return html.replace(
      /<table([^>]*)\s+style\s*=\s*["']([^"']*)["']([^>]*)>/gi,
      (_match, before: string, styleContent: string, after: string) => {
        const cleanedStyle = styleContent
          .split(';')
          .map((prop) => prop.trim())
          .filter((prop) => prop && !/^height\s*:/i.test(prop))
          .join('; ')
          .replace(/;\s*;/g, ';')
          .replace(/^;\s*|\s*;$/g, '');
        if (cleanedStyle) {
          return `<table${before} style="${cleanedStyle}"${after}>`;
        }
        return `<table${before}${after}>`;
      },
    );
  }
};

/** 로컬 오늘 00:00 (datetime-local) — 스케줄 공지 게시 시작 기본값 */
export const getLocalStartOfTodayDatetime = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00`;
};

/**
 * 스케줄 공지 게시 시작 — 레거시 `dayjs().startOf('day')`.
 * 시각은 항상 00:00 (날짜만 유지).
 */
export const toScheduleNoticePublishStartLocal = (
  datetimeLocal?: string | null,
): string => {
  const { date } = splitDatetimeLocal(datetimeLocal ?? '');
  if (date) return composeDatetimeLocal(date, '00:00');
  return getLocalStartOfTodayDatetime();
};

/** datetime-local → API ISO (KST +9h 보정, 레거시 동일) */
export const toAnnouncementPublishIso = (localValue: string): string => {
  const d = new Date(localValue);
  d.setHours(d.getHours() + 9);
  return d.toISOString();
};

/**
 * 스케줄 공지 저장 시 게시 시작 — 레거시
 * `dayjs().startOf('day').add(9, 'hour').toJSON()`
 */
export const getScheduleNoticePublishStartIso = (): string =>
  toAnnouncementPublishIso(getLocalStartOfTodayDatetime());
